import React, {
  useMemo,
  useState,
} from "react";

import {
  countWordsFromManuscriptEntry,
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";


const REVIEW_WINDOW_SIZE =
  6;


function clean(value) {
  return String(
    value || ""
  ).trim();
}



function unitTypeLabel(
  value,
  isFR
) {
  const labels = {
    single_page:
      isFR
        ? "Page simple"
        : "Single page",

    double_page_spread:
      isFR
        ? "Double page"
        : "Two-page spread",

    front_matter:
      isFR
        ? "Page liminaire"
        : "Front matter page",

    back_matter:
      isFR
        ? "Page finale"
        : "Back matter page",

    activity_page:
      isFR
        ? "Page d’activité"
        : "Activity page",
  };

  return (
    labels[
      clean(value)
    ] ||
    (
      isFR
        ? "Page ou double page"
        : "Page or spread"
    )
  );
}


function unitDisplayLabel(
  unit,
  isFR
) {
  const type =
    unitTypeLabel(
      unit?.unitType,
      isFR
    );

  const position =
    clean(
      unit?.pageNumber
    );

  const pageNumber =
    Number(position);

  if (
    !position ||
    !Number.isInteger(
      pageNumber
    ) ||
    pageNumber < 1
  ) {
    return position
      ? `${type} — ${position}`
      : type;
  }

  if (
    clean(
      unit?.unitType
    ) ===
    "double_page_spread"
  ) {
    return `${type} — Pages ${
      pageNumber
    } ${
      isFR
        ? "à"
        : "to"
    } ${
      pageNumber + 1
    }`;
  }

  return `${type} — Page ${
    pageNumber
  }`;
}


export default function ChildrensBookImportedManuscriptResult({
  lang = "en",
  bookMapUnits = [],
  manuscriptPlan = {
    entries: [],
  },
  summary = {},
  message = "",
  onContinue,
}) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const units =
    Array.isArray(
      bookMapUnits
    )
      ? bookMapUnits
      : [];

  const entries =
    useMemo(
      () =>
        Array.isArray(
          manuscriptPlan?.entries
        )
          ? manuscriptPlan.entries
          : [],
      [
        manuscriptPlan?.entries,
      ]
    );

  const entriesByUnit =
    useMemo(
      () =>
        new Map(
          entries.map(
            (entry) => [
              clean(
                entry?.unitId
              ),
              entry,
            ]
          )
        ),
      [entries]
    );

  const affectedIds =
    Array.isArray(
      summary
        ?.affectedUnitIds
    )
      ? summary
          .affectedUnitIds
          .map(clean)
          .filter(Boolean)
      : [];

  const initialStart =
    (() => {
      const firstAffectedIndex =
        units.findIndex(
          (unit) =>
            affectedIds.includes(
              clean(
                unit?.id
              )
            )
        );

      if (
        firstAffectedIndex < 0
      ) {
        return 0;
      }

      return (
        Math.floor(
          firstAffectedIndex /
            REVIEW_WINDOW_SIZE
        ) *
        REVIEW_WINDOW_SIZE
      );
    })();

  const [
    windowStart,
    setWindowStart,
  ] = useState(
    initialStart
  );

  const windowEnd =
    Math.min(
      units.length,
      windowStart +
        REVIEW_WINDOW_SIZE
    );

  const visibleUnits =
    units.slice(
      windowStart,
      windowEnd
    );

  const hasSeveralWindows =
    units.length >
    REVIEW_WINDOW_SIZE;

  const reviewPositionLabel =
    units.length === 1
      ? unitDisplayLabel(
          units[0],
          isFR
        )
      : hasSeveralWindows
        ? `${
            isFR
              ? "Affichage"
              : "Showing"
          } ${
            windowStart + 1
          }–${
            windowEnd
          } ${
            isFR
              ? "sur"
              : "of"
          } ${
            units.length
          } ${
            isFR
              ? "pages et doubles pages"
              : "pages and spreads"
          }`
        : `${
            units.length
          } ${
            isFR
              ? "pages et doubles pages"
              : "pages and spreads"
          }`;

  const ui = {
    title: isFR
      ? "Votre manuscrit importé"
      : "Your Imported Manuscript",

    guidance: isFR
      ? "Découvrez comment votre manuscrit apparaît maintenant dans les pages de votre livre."
      : "Review how your manuscript now appears across the pages of your book.",

    empty: isFR
      ? "Aucun texte de manuscrit pour le moment."
      : "No manuscript text yet.",

    previous: isFR
      ? "Pages précédentes"
      : "Previous pages",

    next: isFR
      ? "Pages suivantes"
      : "Next pages",

    continue: isFR
      ? "Continuer la rédaction"
      : "Continue Writing",

    showing: isFR
      ? "Affichage"
      : "Showing",

    of: isFR
      ? "sur"
      : "of",

    pagesAndSpreads: isFR
      ? "pages et doubles pages"
      : "pages and spreads",

    words: isFR
      ? "mots"
      : "words",

    noStructure: isFR
      ? "Aucune page n’est encore disponible dans la structure du livre."
      : "No pages are available in the Book Structure yet.",
  };

  return (
    <section
      className="ns-card ns-nbb-major-panel"
      aria-labelledby="imported-manuscript-result-title"
      style={{
        padding: 22,
        borderRadius: 18,
        border:
          "1px solid rgba(16,185,129,0.34)",
        background:
          "linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%)",
        marginBottom: 18,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 18,
          flexWrap: "wrap",
          marginBottom: 22,
        }}
      >
        <div
          style={{
            maxWidth: 760,
          }}
        >
          <div
            style={{
              display:
                "inline-flex",
              alignItems:
                "center",
              gap: 8,
              padding:
                "5px 10px",
              borderRadius: 999,
              background:
                "#d1fae5",
              color:
                "#065f46",
              fontSize: 12,
              fontWeight: 800,
              marginBottom: 10,
            }}
          >
            ✓{" "}
            {isFR
              ? "Import terminé"
              : "Import complete"}
          </div>

          <h2
            id="imported-manuscript-result-title"
            style={{
              margin:
                "0 0 8px",
              fontSize:
                "clamp(24px, 3vw, 34px)",
              lineHeight: 1.15,
              color: "#0f172a",
            }}
          >
            {ui.title}
          </h2>

          <p
            style={{
              margin: 0,
              color: "#475569",
              lineHeight: 1.65,
              fontSize: 15,
            }}
          >
            {ui.guidance}
          </p>
        </div>

        {clean(message) ? (
          <div
            role="status"
            style={{
              maxWidth: 430,
              padding:
                "11px 14px",
              borderRadius: 12,
              border:
                "1px solid #a7f3d0",
              background:
                "#f0fdf4",
              color:
                "#166534",
              lineHeight: 1.5,
              fontSize: 14,
            }}
          >
            {message}
          </div>
        ) : null}
      </header>

      {units.length > 0 ? (
        <>
          <div
            style={{
              display:
                units.length > 1
                  ? "flex"
                  : "none",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <p
              className="ns-card-meta"
              style={{
                margin: 0,
              }}
            >
              {units.length > 1
                ? reviewPositionLabel
                : null}
            </p>

            {hasSeveralWindows ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    windowStart ===
                    0
                  }
                  onClick={() =>
                    setWindowStart(
                      Math.max(
                        0,
                        windowStart -
                          REVIEW_WINDOW_SIZE
                      )
                    )
                  }
                >
                  {ui.previous}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    windowEnd >=
                    units.length
                  }
                  onClick={() =>
                    setWindowStart(
                      Math.min(
                        Math.max(
                          0,
                          units.length -
                            REVIEW_WINDOW_SIZE
                        ),
                        windowStart +
                          REVIEW_WINDOW_SIZE
                      )
                    )
                  }
                >
                  {ui.next}
                </button>
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gap: 24,
            }}
          >
            {visibleUnits.map(
              (unit) => {
                const unitId =
                  clean(
                    unit?.id
                  );

                const entry =
                  entriesByUnit.get(
                    unitId
                  ) || {};

                const text =
                  plainTextFromManuscriptEntry(
                    entry
                  );

                const words =
                  countWordsFromManuscriptEntry(
                    entry
                  );

                const label =
                  unitDisplayLabel(
                    unit,
                    isFR
                  );

                const title =
                  clean(
                    unit
                      ?.workingTitle
                  );

                const isSpread =
                  clean(
                    unit?.unitType
                  ) ===
                  "double_page_spread";

                const pageNumber =
                  Number(
                    clean(
                      unit
                        ?.pageNumber
                    )
                  );

                return (
                  <article
                    key={
                      unitId ||
                      label
                    }
                    aria-label={
                      label
                    }
                    style={{
                      padding: 16,
                      borderRadius: 16,
                      border:
                        affectedIds.includes(
                          unitId
                        )
                          ? "2px solid rgba(37,99,235,0.42)"
                          : "1px solid rgba(148,163,184,0.32)",
                      background:
                        "#f8fafc",
                      boxShadow:
                        "0 8px 22px rgba(15,23,42,0.06)",
                    }}
                  >
                    <div
                      style={{
                        display:
                          "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "flex-start",
                        gap: 12,
                        flexWrap:
                          "wrap",
                        marginBottom:
                          12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight:
                              800,
                            color:
                              "#1e3a8a",
                          }}
                        >
                          {label}
                        </div>

                        {title ? (
                          <h3
                            style={{
                              margin:
                                "5px 0 0",
                              color:
                                "#0f172a",
                              fontSize:
                                18,
                            }}
                          >
                            {title}
                          </h3>
                        ) : null}
                      </div>

                      <span
                        style={{
                          fontSize:
                            12,
                          color:
                            "#64748b",
                        }}
                      >
                        {words.toLocaleString()}{" "}
                        {ui.words}
                      </span>
                    </div>

                    {isSpread ? (
                      <div
                        style={{
                          border:
                            "1px solid #cbd5e1",
                          borderRadius:
                            12,
                          overflow:
                            "hidden",
                          background:
                            "linear-gradient(90deg, #ffffff 0%, #ffffff 49.55%, #dbe3ee 49.55%, #dbe3ee 50.45%, #ffffff 50.45%, #ffffff 100%)",
                          boxShadow:
                            "0 10px 28px rgba(15,23,42,0.08)",
                        }}
                      >
                        <div
                          style={{
                            display:
                              "grid",
                            gridTemplateColumns:
                              "1fr 1fr",
                            borderBottom:
                              "1px solid #e2e8f0",
                            color:
                              "#64748b",
                            fontSize:
                              12,
                            fontWeight:
                              700,
                          }}
                        >
                          <div
                            style={{
                              padding:
                                "8px 18px",
                            }}
                          >
                            {Number.isInteger(
                              pageNumber
                            )
                              ? `${
                                  isFR
                                    ? "Page"
                                    : "Page"
                                } ${pageNumber}`
                              : ""}
                          </div>

                          <div
                            style={{
                              padding:
                                "8px 18px",
                              textAlign:
                                "right",
                            }}
                          >
                            {Number.isInteger(
                              pageNumber
                            )
                              ? `${
                                  isFR
                                    ? "Page"
                                    : "Page"
                                } ${
                                  pageNumber +
                                  1
                                }`
                              : ""}
                          </div>
                        </div>

                        <div
                          style={{
                            minHeight:
                              250,
                            padding:
                              "26px 34px 32px",
                            columnCount:
                              2,
                            columnGap:
                              58,
                            whiteSpace:
                              "pre-wrap",
                            fontFamily:
                              'Georgia, "Times New Roman", serif',
                            fontSize:
                              16,
                            lineHeight:
                              1.8,
                            color:
                              "#1e293b",
                          }}
                        >
                          {text ||
                            ui.empty}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          maxWidth:
                            650,
                          minHeight:
                            260,
                          margin:
                            "0 auto",
                          padding:
                            "30px 36px",
                          border:
                            "1px solid #cbd5e1",
                          borderRadius:
                            10,
                          background:
                            "#ffffff",
                          boxShadow:
                            "0 10px 28px rgba(15,23,42,0.08)",
                          whiteSpace:
                            "pre-wrap",
                          fontFamily:
                            'Georgia, "Times New Roman", serif',
                          fontSize:
                            16,
                          lineHeight:
                            1.8,
                          color:
                            "#1e293b",
                        }}
                      >
                        {text ||
                          ui.empty}
                      </div>
                    )}
                  </article>
                );
              }
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
              marginTop: 22,
            }}
          >
            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={
                onContinue
              }
            >
              {ui.continue}
            </button>
          </div>
        </>
      ) : (
        <p
          style={{
            margin: 0,
            padding: 18,
            borderRadius: 12,
            background:
              "#ffffff",
            color:
              "#475569",
          }}
        >
          {ui.noStructure}
        </p>
      )}
    </section>
  );
}
